import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';

interface Practitioner {
  id: string;
  name: string;
  title?: string;
  specialties?: string;
  description?: string;
  photo_url?: string;
}

interface PractitionerSelectionProps {
  practitioners: Practitioner[];
  selectedPractitioner: Practitioner | null;
  onPractitionerSelect: (practitioner: Practitioner) => void;
}

const PractitionerSelection: React.FC<PractitionerSelectionProps> = ({
  practitioners,
  selectedPractitioner,
  onPractitionerSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate initial based on name (for avatar fallback)
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  // Generate a consistent color based on practitioner id
  const getAvatarColor = (id: string) => {
    const colors = [
      '#f44336', '#e91e63', '#9c27b0', '#673ab7', 
      '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
      '#009688', '#4caf50', '#8bc34a', '#cddc39', 
      '#ffc107', '#ff9800', '#ff5722'
    ];
    
    // Simple hash function to get a consistent index
    const hash = id.split('').reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);
    
    return colors[hash % colors.length];
  };

  if (practitioners.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          No Practitioners Available
        </Typography>
        <Typography color="text.secondary">
          There are no practitioners available for the selected service.
          Please try a different service or contact us directly to schedule.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Practitioner
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a practitioner for your appointment
      </Typography>

      <Grid container spacing={2}>
        {practitioners.map((practitioner) => (
          <Grid item xs={12} sm={6} md={4} key={practitioner.id}>
            <Card 
              variant={selectedPractitioner?.id === practitioner.id ? "elevation" : "outlined"}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                borderColor: selectedPractitioner?.id === practitioner.id 
                  ? theme.palette.primary.main 
                  : theme.palette.divider,
                borderWidth: selectedPractitioner?.id === practitioner.id ? 2 : 1,
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  transform: 'translateY(-4px)'
                },
              }}
              onClick={() => onPractitionerSelect(practitioner)}
            >
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {practitioner.photo_url ? (
                  <CardMedia
                    component="img"
                    image={practitioner.photo_url}
                    alt={practitioner.name}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      borderRadius: '50%',
                      objectFit: 'cover',
                      mb: 2 
                    }}
                  />
                ) : (
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120,
                      bgcolor: getAvatarColor(practitioner.id),
                      fontSize: '2rem',
                      mb: 2
                    }}
                  >
                    {getInitials(practitioner.name)}
                  </Avatar>
                )}
                
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 0 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {practitioner.name}
                  </Typography>
                  
                  {practitioner.title && (
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {practitioner.title}
                    </Typography>
                  )}
                  
                  {practitioner.specialties && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {practitioner.specialties}
                    </Typography>
                  )}
                  
                  {practitioner.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {practitioner.description.length > 120 
                        ? `${practitioner.description.substring(0, 120)}...` 
                        : practitioner.description}
                    </Typography>
                  )}
                </CardContent>
              </Box>
              
              <Box sx={{ p: 2, pt: 0 }}>
                <Button 
                  variant={selectedPractitioner?.id === practitioner.id ? "contained" : "outlined"}
                  color="primary"
                  fullWidth
                >
                  {selectedPractitioner?.id === practitioner.id ? "Selected" : "Select"}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PractitionerSelection;